import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  isSupportedLanguage,
  type SupportedLanguage,
} from "./config";
import { buildLocalizedPath } from "./useLocalizedPath";
import { persistLanguage } from "./storage";
import { writeProfileLanguage } from "./ProfileLanguageSync";
import { useAuth } from "@/hooks/useAuth";

interface LanguageSwitcherProps {
  variant?: "ghost" | "outline";
  size?: "default" | "sm" | "icon";
  className?: string;
}

export const LanguageSwitcher = ({
  variant = "ghost",
  size = "sm",
  className,
}: LanguageSwitcherProps) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const { user } = useAuth();

  const current: SupportedLanguage = isSupportedLanguage(i18n.language)
    ? i18n.language
    : DEFAULT_LANGUAGE;

  const labelFor = (lng: SupportedLanguage) =>
    lng === "en" ? t("language.english") : t("language.spanish");

  const switchTo = (lng: SupportedLanguage) => {
    if (lng === current) return;
    persistLanguage(lng);
    void i18n.changeLanguage(lng);
    if (user?.id) void writeProfileLanguage(user.id, lng);
    const isPublicRoute = !pathname.startsWith("/dashboard");
    if (isPublicRoute) {
      const next = buildLocalizedPath(pathname, lng) + search + hash;
      navigate(next, { replace: true });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          aria-label={t("language.label") ?? "Language"}
        >
          <Languages className="h-4 w-4" />
          <span className="ml-1 text-xs uppercase">{current}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {SUPPORTED_LANGUAGES.map((lng) => (
          <DropdownMenuItem
            key={lng}
            onSelect={() => switchTo(lng)}
            data-active={lng === current}
            className="data-[active=true]:font-semibold"
          >
            {labelFor(lng)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
